import { useState } from "react";
import { useRH } from "../store/rh";
import { useSettings } from "../store/settings";
import { generatePerformanceReviewAI } from "../lib/claude";
import { PERSONAS } from "../data/org";
import { initials, monogramColor } from "../lib/monogram";

function fmt(ts: number) {
  return new Date(ts).toLocaleDateString("pt-BR", {
    day: "2-digit", month: "2-digit", year: "numeric"
  });
}

export function ReviewModal({ personaId, onClose }: { personaId: string; onClose: () => void }) {
  const persona     = PERSONAS.find(p => p.id === personaId);
  const employee    = useRH(s => s.employees[personaId]);
  const addReview   = useRH(s => s.addReview);
  const removeReview= useRH(s => s.removeReview);
  const apiKey      = useSettings(s => s.anthropicKey);
  const model       = useSettings(s => s.model);

  const [rating, setRating] = useState(4);
  const [note, setNote]     = useState("");
  const [text, setText]     = useState("");
  const [loading, setLoading] = useState(false);
  const [useAI, setUseAI]   = useState(true);

  if (!persona) return null;

  const reviews = employee?.reviews ?? [];
  const avg = reviews.length
    ? (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1)
    : "—";

  const generate = async () => {
    if (!useAI || !apiKey) {
      setText(
        `▸ NOTA ATRIBUÍDA: ${rating}/5\n\n` +
        `▸ OBSERVAÇÃO MANUAL\n${note || "(sem observação)"}\n\n` +
        `▸ FEEDBACK\nReview manual — escreva aqui sua avaliação detalhada de ${persona.name}.`
      );
      return;
    }
    try {
      setLoading(true);
      const t = await generatePerformanceReviewAI({
        apiKey, model, personaId, rating, managerNote: note
      });
      setText(t);
    } catch (e) {
      alert("Falha na API: " + (e as Error).message);
    } finally {
      setLoading(false);
    }
  };

  const save = () => {
    if (!text.trim()) { alert("Gere ou escreva o texto antes de salvar."); return; }
    addReview(personaId, { rating, managerNote: note, text: text.trim() });
    setText(""); setNote(""); setRating(4);
    alert("Review salva.");
  };

  return (
    <div className="fixed inset-0 z-50 bg-ink/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="bg-bone border-2 border-ink w-full max-w-3xl max-h-[90vh] overflow-y-auto
                      shadow-[6px_6px_0_0_#121214]"
           onClick={(e) => e.stopPropagation()}>
        <header className="bg-ink text-bone px-5 py-4 flex items-center justify-between gap-3 sticky top-0 z-10">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-12 h-12 flex items-center justify-center font-display font-black text-lg shrink-0"
                 style={{ background: monogramColor(persona.id), color: "#F2EEE6" }}>
              {initials(persona.name)}
            </div>
            <div className="min-w-0">
              <div className="font-mono text-xs text-gold">REVIEW-01</div>
              <h2 className="font-display font-black uppercase text-2xl leading-none truncate">
                {persona.name}
              </h2>
              <div className="font-crimson italic text-bone/70 text-sm truncate">{persona.bio}</div>
            </div>
          </div>
          <button onClick={onClose} className="btn bg-bone text-ink border-bone shrink-0">fechar ×</button>
        </header>

        <div className="p-5 space-y-5">
          {/* avg */}
          {reviews.length > 0 && (
            <div className="border-2 border-ink px-4 py-3 flex items-center justify-between">
              <div>
                <div className="tag text-graydk">média histórica</div>
                <div className="font-display font-black text-4xl leading-none">{avg}/5</div>
              </div>
              <div className="tag text-graydk">{reviews.length} review{reviews.length > 1 ? "s" : ""}</div>
            </div>
          )}

          {/* nova review */}
          <section className="border-2 border-ink p-4">
            <div className="font-display font-bold uppercase text-lg mb-3">Nova avaliação</div>

            <div className="tag text-graydk mb-1">nota</div>
            <div className="flex gap-1 mb-4">
              {[1,2,3,4,5].map(n => (
                <button
                  key={n}
                  onClick={() => setRating(n)}
                  className={`w-10 h-10 border-2 border-ink font-display font-black text-xl
                    ${n <= rating ? "bg-red text-bone" : "bg-bone text-ink"}`}>
                  {n}
                </button>
              ))}
            </div>

            <div className="tag text-graydk mb-1">observação do Marcio (opcional, vira input pro Claude)</div>
            <textarea
              value={note}
              onChange={(e) => setNote(e.target.value)}
              rows={2}
              className="w-full border border-ink bg-bone px-3 py-2 font-mono text-[13px] resize-none mb-3"
              placeholder="ex: tem entregado lançamentos no prazo, mas o ROAS caiu nos últimos 2 meses…"
            />

            <label className="flex items-center gap-2 cursor-pointer tag mb-3">
              <input type="checkbox" checked={useAI} onChange={(e) => setUseAI(e.target.checked)} />
              gerar com Claude {!apiKey && <span className="text-red">(sem chave)</span>}
            </label>

            <button
              onClick={generate}
              disabled={loading}
              className="btn bg-ink text-bone border-ink disabled:opacity-50 mb-3">
              {loading ? "gerando…" : (useAI && apiKey ? "🤖 gerar review" : "▸ preencher manual")}
            </button>

            {text && (
              <>
                <div className="tag text-graydk mb-1">texto da review (edite se quiser)</div>
                <textarea
                  value={text}
                  onChange={(e) => setText(e.target.value)}
                  rows={10}
                  className="w-full border border-ink bg-bone px-3 py-2 font-mono text-[13px] resize-y mb-3"
                />
                <button onClick={save} className="btn btn-hire">↳ salvar review</button>
              </>
            )}
          </section>

          {/* histórico */}
          {reviews.length > 0 && (
            <section>
              <div className="font-display font-bold uppercase text-lg mb-2">Reviews anteriores</div>
              <div className="space-y-2">
                {reviews.map(r => (
                  <div key={r.id} className="border-2 border-ink bg-bone">
                    <div className="px-3 py-2 border-b border-ink/30 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <span className="font-display font-black text-2xl">{r.rating}/5</span>
                        <span className="tag text-graydk">{fmt(r.at)}</span>
                      </div>
                      <button
                        onClick={() => { if (confirm("Apagar esta review?")) removeReview(personaId, r.id); }}
                        className="tag text-red underline">apagar</button>
                    </div>
                    {r.managerNote && (
                      <div className="px-3 pt-2 font-crimson italic text-paper text-sm">
                        obs: {r.managerNote}
                      </div>
                    )}
                    <pre className="p-3 font-mono text-[12px] whitespace-pre-wrap leading-relaxed">{r.text}</pre>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
      </div>
    </div>
  );
}
